import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import JumboConfirmDialog from "@jumbo/components/JumboConfirmDialog";
import { ROUTES } from "app/utils/constants/routeConstants";

const useLoginRedirect = ({
  isLoggedIn,
  dialogTitle = "Login Required",
  dialogMessage = "Please login to continue.",
  cancelText = "Cancel",
  confirmText = "Login",
  cancelColor = "error",
  confirmColor = "primary",
}) => {
  const navigate = useNavigate();
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [redirectConfig, setRedirectConfig] = useState({});

  const handleLoginDialogOpen = useCallback((config = {}) => {
    setRedirectConfig(config);
    setOpenLoginDialog(true);
  }, []);

  const handleLoginDialogClose = useCallback(() => {
    setOpenLoginDialog(false);
    setRedirectConfig({});
  }, []);

  const handleLoginConfirm = useCallback(() => {
    const { redirectPath = ROUTES.REPORT, queryParams = {} } = redirectConfig;
    const queryString = new URLSearchParams(queryParams).toString();
    const redirectURI = encodeURIComponent(
      queryString ? `${redirectPath}?${queryString}` : redirectPath
    );
    const redirectUrl = `${ROUTES.USER_LOGIN}?redirectURI=${redirectURI}`;
    navigate(redirectUrl);
    handleLoginDialogClose();
  }, [navigate, redirectConfig, handleLoginDialogClose]);

  const handleProtectedAction = useCallback(
    (targetPath, options = {}) => {
      if (!isLoggedIn) {
        const { queryParams = {} } = options;
        handleLoginDialogOpen({ redirectPath: targetPath, queryParams });
      } else {
        const { queryParams = {} } = options;
        const queryString = new URLSearchParams(queryParams).toString();
        navigate(queryString ? `${targetPath}?${queryString}` : targetPath);
      }
    },
    [isLoggedIn, navigate, handleLoginDialogOpen]
  );

  const LoginDialog = (
    <JumboConfirmDialog
      open={openLoginDialog}
      onClose={handleLoginDialogClose}
      onConfirm={handleLoginConfirm}
      title={dialogTitle}
      message={dialogMessage}
      cancelText={cancelText}
      confirmText={confirmText}
      cancelColor={cancelColor}
      confirmColor={confirmColor}
      confirmVariant="contained"
    />
  );

  return {
    handleProtectedAction,
    LoginDialog,
  };
};

export default useLoginRedirect;
